import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiUrl } from '../utils/api';
import { AdminView } from '../engazeiq-admin-suite/types';
import { Settings, Moon, Sun, Monitor, Bell, Shield, X, Eye, Upload, Trash2, Camera, User, LayoutDashboard, Home, LogOut } from 'lucide-react';

const UserProfileContainer = styled.div`
  position: relative;
`;

const PreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const ProfilePreview = styled.div<{ $imageUrl?: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #f0f0f0;
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  border: 3px solid #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: #999;
  font-weight: 600;
`;

const UserAvatar = styled.div<{ $imageUrl?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #0A2540;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 50px;
  right: 0;
  width: 280px;
  background-color: var(--surface-color);
  color: var(--primary-color);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 8px;
  display: ${props => (props.$isOpen ? 'block' : 'none')};
  z-index: 100;
  backdrop-filter: blur(12px);
`;

const UserInfo = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: 16px;
  margin-bottom: 8px;
  border-radius: 8px;
`;

const UserName = styled.h3`
  font-size: 16px;
  margin: 0 0 4px 0;
  font-weight: 600;
`;

const UserEmail = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  margin: 0;
`;

const UserRole = styled.div`
  font-size: 10px;
  background-color: var(--bg-main);
  border: 1px solid var(--border-color);
  color: var(--primary-color);
  padding: 4px 10px;
  border-radius: 9999px;
  display: inline-block;
  margin-top: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MenuList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li`
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--hover-color);
    transform: translateX(2px);
  }
  
  &.logout {
    color: #ef4444;
    margin-top: 4px;
    border-top: 1px solid var(--border-color);
    border-radius: 0 0 8px 8px;
    padding-top: 14px;
    &:hover {
      background-color: rgba(239, 68, 68, 0.05);
    }
  }
`;

const MenuItemIcon = styled.span`
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  width: 18px;
  
  .logout & {
    color: #ef4444;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;
const ModalContent = styled.div`
  background: var(--surface-color); color: var(--primary-color); padding: 24px; border-radius: 12px; width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid var(--border-color);
`;
const ModalTitle = styled.h2`
  margin-top: 0; font-size: 20px; margin-bottom: 20px;
`;
const FormGroup = styled.div`
  margin-bottom: 16px;
`;
const Label = styled.label`
  display: block; margin-bottom: 8px; font-weight: 500; font-size: 14px;
`;
const Input = styled.input`
  width: 100%; padding: 8px 12px; 
  background-color: var(--surface-muted);
  color: var(--primary-color);
  border: 1px solid var(--border-color); 
  border-radius: 8px; font-size: 14px;
  &:focus { outline: none; border-color: var(--accent-color); }
`;
const ButtonGroup = styled.div`
  display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px;
`;
const Button = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer; font-weight: 500;
  background-color: ${props => props.$primary ? '#FF8000' : '#f5f5f5'};
  color: ${props => props.$primary ? 'white' : '#333'};
  &:hover { background-color: ${props => props.$primary ? '#e67300' : '#e0e0e0'}; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const PhotoMenuContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  border: 1px solid #e2e8f0;
  width: 200px;
  z-index: 1000;
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translate(-50%, -10px); }
    to { opacity: 1; transform: translate(-50%, 0); }
  }
`;

const PhotoMenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #4a5568;
  transition: all 0.2s;
  text-align: left;

  &:hover {
    background: #f7fafc;
    color: #2d3748;
  }

  &.remove {
    color: #e53e3e;
    &:hover {
      background: #fff5f5;
    }
  }

  svg {
    color: #718096;
  }
`;

const EditPhotoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  text-align: center;
  padding: 10px;

  &:hover {
    opacity: 1;
  }
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: pointer;
`;

const FullImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  border-radius: 8px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
`;

const CloseImageButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255,255,255,0.1);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;


interface UserProfileProps {
  onViewChange?: (view: AdminView) => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onViewChange }) => {
  const { user, logout, login, token, updatePreferences } = useAuth() as any;
  const navigate = useNavigate();
  const location = window.location;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isUserSettingsModalOpen, setIsUserSettingsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ username: '', email: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPhotoMenuOpen, setIsPhotoMenuOpen] = useState(false);
  const [isFullImageOpen, setIsFullImageOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isProfileModalOpen && user) {
      setFormData({
        username: user.name || (user as any).username || '',
        email: user.email || ''
      });
      setPreviewUrl(null);
    }
  }, [isProfileModalOpen, user]);

  const handleSave = async () => {
    if (!user || !token) return;
    setIsSaving(true);
    try {
      const response = await axios.put(apiUrl(`/api/auth/users/${user.id}`), {
        username: formData.username,
        email: formData.email,
        role: user.role,
        company_name: (user as any).company_name
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const updatedUser = {
        ...response.data,
        name: response.data.username,
        token: token,
        preferences: user.preferences
      };

      login(updatedUser);
      alert('Profile updated successfully!');
      // No logout needed
    } catch (err) {
      console.error('Failed to update profile:', err);
      alert('Failed to update profile');
    } finally {
      setIsSaving(false);
      setIsProfileModalOpen(false);
      setIsOpen(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user || !token) return;

    const file = e.target.files[0];

    // Create immediate preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsSaving(true);
      const response = await axios.post(apiUrl(`/api/auth/users/${user.id}/profile-picture`), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const updatedUser = {
        ...response.data,
        name: response.data.username,
        token: token,
        preferences: user.preferences
      };

      login(updatedUser);
      // alert('Profile picture updated!'); 
    } catch (err) {
      console.error('Failed to upload picture:', err);
      alert('Failed to upload picture');
      setPreviewUrl(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!user || !token) return;

    if (!window.confirm('Are you sure you want to remove your profile picture?')) return;

    setIsSaving(true);
    try {
      const response = await axios.delete(apiUrl(`/api/auth/users/${user.id}/profile-picture`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const updatedUser = {
        ...response.data,
        name: response.data.username || user.name,
        token: token,
        preferences: user.preferences
      };

      login(updatedUser);
      setPreviewUrl(null);
      setIsPhotoMenuOpen(false);
      alert('Profile picture removed successfully!');
    } catch (err) {
      console.error('Failed to remove profile picture:', err);
      alert('Failed to remove profile picture');
    } finally {
      setIsSaving(false);
    }
  };

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
      if (photoMenuRef.current && !photoMenuRef.current.contains(event.target as Node)) {
        setIsPhotoMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get initials from user's name or email
  const getInitials = () => {
    if (!user) return 'U';

    // If user has a name, use it
    if (user.name) {
      const nameParts = user.name.trim().split(' ');
      if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
      }
      return `${nameParts[0].charAt(0).toUpperCase()}${nameParts[nameParts.length - 1].charAt(0).toUpperCase()}`;
    }

    // If no name, use first letter of email
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }

    return 'U';
  };

  if (!user) return null;


  return (
    <UserProfileContainer ref={dropdownRef}>
      <UserAvatar onClick={() => setIsOpen(!isOpen)} $imageUrl={user.profile_picture ? apiUrl(user.profile_picture) : undefined}>
        {!user.profile_picture && getInitials()}
      </UserAvatar>

      <DropdownMenu $isOpen={isOpen}>
        <UserInfo>
          <UserName>{user.name}</UserName>
          <UserEmail>{user.email}</UserEmail>
          <UserRole>{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</UserRole>
        </UserInfo>

        <MenuList>
          <MenuItem onClick={() => { setIsProfileModalOpen(true); setIsOpen(false); }}>
            <MenuItemIcon><User size={16} /></MenuItemIcon>
            My Profile
          </MenuItem>
          <MenuItem onClick={() => { setIsUserSettingsModalOpen(true); setIsOpen(false); }}>
            <MenuItemIcon><Settings size={16} /></MenuItemIcon>
            Settings
          </MenuItem>
          <MenuItem onClick={() => { onViewChange?.(AdminView.NOTIFICATIONS); setIsOpen(false); }}>
            <MenuItemIcon><Bell size={16} /></MenuItemIcon>
            Notifications
          </MenuItem>
          {location.pathname.includes('/admin') ? (
            <MenuItem onClick={() => navigate('/')}>
              <MenuItemIcon><Home size={16} /></MenuItemIcon>
              Back to App
            </MenuItem>
          ) : (
            <MenuItem onClick={() => navigate('/admin')}>
              <MenuItemIcon><LayoutDashboard size={16} /></MenuItemIcon>
              Admin Panel
            </MenuItem>
          )}
          <MenuItem className="logout" onClick={logout}>
            <MenuItemIcon><LogOut size={16} /></MenuItemIcon>
            Logout
          </MenuItem>
        </MenuList>
      </DropdownMenu>

      {isProfileModalOpen && (
        <ModalOverlay onClick={() => setIsProfileModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Edit Profile</ModalTitle>
            <PreviewContainer>
              <div style={{ position: 'relative' }} ref={photoMenuRef}>
                <ProfilePreview
                  $imageUrl={previewUrl || (user.profile_picture ? apiUrl(user.profile_picture) : undefined)}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsPhotoMenuOpen(!isPhotoMenuOpen)}
                >
                  {!previewUrl && !user.profile_picture && getInitials()}
                  <EditPhotoOverlay>
                    <Camera size={20} style={{ marginBottom: '4px' }} />
                    <span>Change profile picture</span>
                  </EditPhotoOverlay>
                </ProfilePreview>

                {isPhotoMenuOpen && (
                  <PhotoMenuContainer>
                    <PhotoMenuItem onClick={() => { setIsFullImageOpen(true); setIsPhotoMenuOpen(false); }}>
                      <Eye size={18} />
                      View photo
                    </PhotoMenuItem>
                    <PhotoMenuItem onClick={() => { fileInputRef.current?.click(); setIsPhotoMenuOpen(false); }}>
                      <Upload size={18} />
                      Upload photo
                    </PhotoMenuItem>
                    <PhotoMenuItem className="remove" onClick={handleRemoveProfilePicture}>
                      <Trash2 size={18} />
                      Remove photo
                    </PhotoMenuItem>
                  </PhotoMenuContainer>
                )}
              </div>
            </PreviewContainer>
            <FormGroup style={{ display: 'none' }}>
              <Label>Profile Picture</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
              />
            </FormGroup>
            <FormGroup>
              <Label>Username</Label>
              <Input
                value={formData.username}
                onChange={e => setFormData({ ...formData, username: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </FormGroup>
            <ButtonGroup>
              <Button onClick={() => setIsProfileModalOpen(false)}>Cancel</Button>
              <Button $primary onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}

      {isUserSettingsModalOpen && (
        <ModalOverlay onClick={() => setIsUserSettingsModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <ModalTitle style={{ margin: 0 }}>User Settings</ModalTitle>
              <button
                onClick={() => setIsUserSettingsModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>



            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Preferences
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Bell size={18} color="var(--text-muted)" />
                    <span style={{ fontSize: '14px', color: 'var(--primary-color)' }}>Email Notifications</span>
                  </div>
                  <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Shield size={18} color="var(--text-muted)" />
                    <span style={{ fontSize: '14px', color: 'var(--primary-color)' }}>Privacy Mode</span>
                  </div>
                  <input type="checkbox" style={{ width: '18px', height: '18px' }} />
                </div>
              </div>
            </div>

            <ButtonGroup>
              <Button $primary onClick={() => setIsUserSettingsModalOpen(false)}>Done</Button>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}

      {isFullImageOpen && (
        <ImageModalOverlay onClick={() => setIsFullImageOpen(false)}>
          <CloseImageButton onClick={() => setIsFullImageOpen(false)}>
            <X size={24} />
          </CloseImageButton>
          <FullImage
            src={previewUrl || (user.profile_picture ? apiUrl(user.profile_picture) : undefined)}
            onClick={e => e.stopPropagation()}
          />
        </ImageModalOverlay>
      )}
    </UserProfileContainer>
  );
};

export default UserProfile;
